// src/components/auth/ResetPasswordHandler.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import styles from "./ResetPasswordHandler.module.css"; // Import the CSS module
import PasswordValidation from "../loginforms/PasswordValidation"; // Import your existing validator

const ResetPasswordHandler = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [invalidLink, setInvalidLink] = useState(false);
  
  const navigate = useNavigate();
  
  // Get the action code from the URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const oobCode = queryParams.get("oobCode");
  
  useEffect(() => {
    // Verify the password reset code is valid
    const auth = getAuth();
    if (oobCode) {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setEmail(email);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error verifying reset code:", error);
          setInvalidLink(true);
          setError("This password reset link is invalid or has expired.");
          setLoading(false);
        });
    } else {
      setInvalidLink(true);
      setError("No reset code found in URL.");
      setLoading(false);
    }
  }, [oobCode]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    
    // Check password requirements - this is redundant because PasswordValidation
    // component will prevent weak passwords, but we keep it as a fallback
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    // Check for password complexity
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(newPassword);
    
    if (!(hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar)) {
      setError("Password must include uppercase, lowercase, numbers, and special characters.");
      return;
    }
    
    setLoading(true);
    
    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);
      setError("");
    } catch (error) {
      console.error("Error resetting password:", error);
      setError("Failed to reset password. This link may have expired.");
    } finally {
      setLoading(false);
    }
  };
  
  // Direct navigation to the main site
  const handleBackToLogin = () => {
    // Use window.location to redirect to the absolute URL
    window.location.href = "https://linkist.se/";
  };
  
  if (loading) {
    return (
      <div className={styles.resetPasswordContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Please wait...</p>
        </div>
      </div>
    );
  }
  
  if (invalidLink) {
    return (
      <div className={styles.resetPasswordContainer}>
        <h2>Invalid Reset Link</h2>
        <p className={styles.errorText}>{error}</p>
        <button onClick={handleBackToLogin} className={styles.button}>
          Back to Login
        </button>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className={styles.resetPasswordContainer}>
        <h2>Password Reset Successful</h2>
        <p className={styles.successText}>
          Your password has been reset successfully. You can now log in with your new password.
        </p>
        <button onClick={handleBackToLogin} className={styles.button}>
          Go to Login
        </button>
      </div>
    );
  }
  
  return (
    <div className={styles.resetPasswordContainer}>
      <h2>Reset Your Password</h2>
      {email && <p className={styles.emailInfo}>Email: {email}</p>}
      
      {error && <p className={styles.errorText}>{error}</p>}
      
      <form onSubmit={handleSubmit} className={styles.resetForm}>
        <div className={styles.formGroup}>
          <label htmlFor="newPassword">New Password</label>
          <input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            className={styles.input}
          />
        </div>
        
        {/* Add your PasswordValidation component */}
        <PasswordValidation 
          password={newPassword}
          setPassword={setNewPassword}
        />
        
        <div className={styles.formGroup}>
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className={styles.input}
          />
        </div>
        
        <div className={styles.formActions}>
          <button 
            type="submit" 
            disabled={loading}
            className={styles.button}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
          
          <button 
            type="button" 
            onClick={handleBackToLogin} 
            className={`${styles.button} ${styles.secondary}`}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordHandler;